import { HttpClient } from '@adapters/HttpClient';

export interface CountResponse {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
}

export interface LogisticStatusCounts {
  all: number;
  no_logistic_status: number;
  in_transit: number;
  in_delivery: number;
  pickup_point: number;
  delivered: number;
  returned_to_sender: number;
}

interface SearchFilterPayload {
  filters: Array<{
    column: string;
    operator: string;
    value?: unknown;
  }>;
  logic: string;
}

function buildAdvancedFiltersParams(searchFilter: SearchFilterPayload) {
  const params: Record<string, unknown> = {};

  if (searchFilter.filters && Array.isArray(searchFilter.filters)) {
    searchFilter.filters.forEach((filter, index) => {
      params[`advanced_filters[filters][${index}][column]`] = filter.column;
      params[`advanced_filters[filters][${index}][operator]`] = filter.operator;

      if (
        filter.value !== undefined &&
        filter.value !== null &&
        !(Array.isArray(filter.value) && filter.value.length === 0)
      ) {
        params[`advanced_filters[filters][${index}][value]`] = filter.value;
      }
    });
  }

  if (searchFilter.logic) {
    params['advanced_filters[logic]'] = searchFilter.logic;
  }

  return params;
}

export class CountsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Obtiene el conteo de pedidos para un estado logístico específico
   * @param shipmentStatusId - ID del estado logístico (opcional para "Todos")
   * @returns Promise<number> - Total de pedidos para ese estado
   */
  async getCountByLogisticStatus(shipmentStatusId?: number, searchFilter?: SearchFilterPayload): Promise<number> {
    try {
      const params: any = {
        per_page: 1, // Solo necesitamos el meta, no los datos
        status_ids: '6,7,8', // Estados de pedidos enviados
        ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
      };

      // Solo agregar shipment_status_ids si se especifica
      if (shipmentStatusId) {
        params.shipment_status_ids = shipmentStatusId;
      }

      const response = await this.httpClient.get<any>('/orders', params);
      return response.meta.total;
    } catch (error: any) {
      console.log('🔍 CountsApiAdapter - Error:', {
        message: error.message,
        response: error.response,
        shipmentStatusId,
      });
      return 0;
    }
  }

  /**
   * Obtiene todos los conteos de estados logísticos en paralelo
   * @returns Promise<LogisticStatusCounts> - Conteos para todos los estados
   */
  async getAllLogisticStatusCounts(searchFilter?: SearchFilterPayload): Promise<LogisticStatusCounts> {
    try {
      // Hacer todas las llamadas en paralelo para mayor eficiencia
      const [
        allCount,
        noLogisticStatusCount,
        inTransitCount,
        inDeliveryCount,
        pickupPointCount,
        deliveredCount,
        returnedToSenderCount,
      ] = await Promise.all([
        this.getCountByLogisticStatus(undefined, searchFilter), // Todos
        this.getCountByLogisticStatus(1, searchFilter), // Sin estado logístico
        this.getCountByLogisticStatus(2, searchFilter), // En tránsito
        this.getCountByLogisticStatus(3, searchFilter), // En reparto
        this.getCountByLogisticStatus(4, searchFilter), // Punto de recogida
        this.getCountByLogisticStatus(5, searchFilter), // Entregado
        this.getCountByLogisticStatus(6, searchFilter), // Devuelto al remitente
      ]);

      return {
        all: allCount,
        no_logistic_status: noLogisticStatusCount,
        in_transit: inTransitCount,
        in_delivery: inDeliveryCount,
        pickup_point: pickupPointCount,
        delivered: deliveredCount,
        returned_to_sender: returnedToSenderCount,
      };
    } catch (error: any) {
      console.log('🔍 CountsApiAdapter - Error getting all counts:', {
        message: error.message,
        response: error.response,
      });

      // Retornar valores por defecto en caso de error
      return {
        all: 0,
        no_logistic_status: 0,
        in_transit: 0,
        in_delivery: 0,
        pickup_point: 0,
        delivered: 0,
        returned_to_sender: 0,
      };
    }
  }
}
